import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Building2, User, Lock, ShieldCheck } from "lucide-react";
import BusinessDemoCard from "../components/BusinessDemoCard";
import LegalAcceptanceModal from "../components/LegalAcceptanceModal";
import {
  ACTIVE_SERVICE_TYPES, COMING_SOON_SERVICE_TYPES, COMING_SOON_MESSAGE,
} from "../lib/serviceTypes";
import NoIndex from "../components/NoIndex";
import { BrandLogo } from "../components/BrandLogo";

// Registration is intentionally split by audience (mirrors /login). The
// `?as=business` query param triggers the business flow; default is customer.
// No tab toggle on the page: the two audiences see only their own form.
export default function Register() {
  const { register, registerCustomer } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const isBiz = params.get("as") === "business";

  const [bizForm, setBizForm] = useState({
    company_name: "", business_type: "car_rental", city: "",
    owner_name: "", email: "", phone: "", password: "",
  });
  const [custForm, setCustForm] = useState({
    name: "", email: "", phone: "", password: "",
  });
  const [rememberDevice, setRememberDevice] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [legalOpen, setLegalOpen] = useState(false);

  const openLegal = (e) => {
    e.preventDefault();
    setErr("");
    if (isBiz) {
      if (!bizForm.company_name.trim() || !bizForm.owner_name.trim() ||
          !bizForm.email.trim() || !bizForm.password) {
        setErr("Please fill in the required fields first.");
        return;
      }
    } else {
      if (!custForm.name.trim() || !custForm.email.trim() || !custForm.password) {
        setErr("Please fill in the required fields first.");
        return;
      }
    }
    setLegalOpen(true);
  };

  const finalSubmit = async ({ legal_bundle_version }) => {
    setLoading(true);
    setErr("");
    try {
      const acceptBlock = { accepted: true, legal_bundle_version };
      if (isBiz) {
        await register({
          ...bizForm,
          terms: acceptBlock,
          business_terms: acceptBlock,
          remember_device: rememberDevice,
        });
        setLegalOpen(false);
        toast.success("Business account created. Welcome to ConfirmPH!");
        navigate("/app");
      } else {
        await registerCustomer({
          ...custForm,
          terms: acceptBlock,
          remember_device: rememberDevice,
        });
        setLegalOpen(false);
        toast.success("Welcome to ConfirmPH!");
        navigate("/portal");
      }
    } catch (e2) {
      const msg = formatApiError(e2);
      setErr(msg);
      toast.error(msg);
      setLegalOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const Icon = isBiz ? Building2 : User;
  const switchHelp = isBiz
    ? { text: "Just renting a car?", action: "Sign up as customer →", to: "/register" }
    : { text: "Run a rental business?", action: "Sign up as business →", to: "/register?as=business" };
  const loginPath = isBiz ? "/login?as=business" : "/login";

  return (
    <div className="max-w-xl mx-auto px-4 sm:px-6 py-12">
      <NoIndex title="Create an account · ConfirmPH" />
      <Link to="/" className="flex justify-center mb-6" data-testid="register-brand-link">
        <BrandLogo
          variant="full"
          className="h-16 w-auto"
          alt="ConfirmPH"
          testId="register-brand-logo"
        />
      </Link>
      <div
        className="rounded-2xl border border-slate-200 bg-white p-8"
        data-testid={isBiz ? "register-page-business" : "register-page-user"}
      >
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-xs font-semibold text-emerald-800">
          <Icon className="w-3.5 h-3.5" /> {isBiz ? "For business owners" : "For renters / customers"}
        </div>

        {isBiz ? (
          <>
            <h1 className="mt-3 font-display text-3xl font-bold">Create your business account</h1>
            <p className="mt-1 text-sm text-slate-600">Free to start. 30-day trial. No credit card needed.</p>
            <form onSubmit={openLegal} className="mt-6 grid sm:grid-cols-2 gap-4" data-testid="register-form-business">
              <Field label="Company name" required testId="register-company-input" value={bizForm.company_name}
                     onChange={(e) => setBizForm({ ...bizForm, company_name: e.target.value })} />
              <Field label="City / area" testId="register-city-input" value={bizForm.city}
                     onChange={(e) => setBizForm({ ...bizForm, city: e.target.value })} />
              <ServiceTypeSelect
                value={bizForm.business_type}
                onChange={(v) => setBizForm({ ...bizForm, business_type: v })}
              />
              <Field label="Owner name" required testId="register-owner-input" value={bizForm.owner_name}
                     onChange={(e) => setBizForm({ ...bizForm, owner_name: e.target.value })} />
              <Field label="Phone" testId="register-phone-input" value={bizForm.phone}
                     onChange={(e) => setBizForm({ ...bizForm, phone: e.target.value })} />
              <Field label="Email" type="email" required testId="register-email-input" value={bizForm.email}
                     onChange={(e) => setBizForm({ ...bizForm, email: e.target.value })} className="sm:col-span-2" />
              <Field label="Password (min 6 chars)" type="password" required testId="register-password-input"
                     value={bizForm.password}
                     onChange={(e) => setBizForm({ ...bizForm, password: e.target.value })} className="sm:col-span-2" />
              <RememberDeviceField checked={rememberDevice} onChange={setRememberDevice} className="sm:col-span-2" />
              <LegalNotice className="sm:col-span-2" mode="business" />
              {err && <div data-testid="register-error" className="sm:col-span-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{err}</div>}
              <button
                data-testid="register-submit-btn"
                disabled={loading}
                className="sm:col-span-2 px-4 py-3 rounded-lg btn-brand font-medium disabled:opacity-60"
              >
                {loading ? "Creating account…" : "Review terms & create business account"}
              </button>
            </form>
          </>
        ) : (
          <>
            <h1 className="mt-3 font-display text-3xl font-bold">Create your customer account</h1>
            <p className="mt-1 text-sm text-slate-600">Track all your bookings, businesses you&apos;ve rented from, and notifications: in one place.</p>
            <form onSubmit={openLegal} className="mt-6 grid gap-4" data-testid="register-form-customer">
              <Field label="Full name" required testId="register-name-input" value={custForm.name}
                     onChange={(e) => setCustForm({ ...custForm, name: e.target.value })} />
              <Field label="Email" type="email" required testId="register-email-input" value={custForm.email}
                     onChange={(e) => setCustForm({ ...custForm, email: e.target.value })} />
              <Field label="Phone (optional)" testId="register-phone-input" value={custForm.phone}
                     onChange={(e) => setCustForm({ ...custForm, phone: e.target.value })} />
              <Field label="Password (min 6 chars)" type="password" required testId="register-password-input"
                     value={custForm.password}
                     onChange={(e) => setCustForm({ ...custForm, password: e.target.value })} />
              <RememberDeviceField checked={rememberDevice} onChange={setRememberDevice} />
              <LegalNotice mode="customer" />
              {err && <div data-testid="register-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{err}</div>}
              <button
                data-testid="register-submit-btn"
                disabled={loading}
                className="px-4 py-3 rounded-lg btn-brand font-medium disabled:opacity-60"
              >
                {loading ? "Creating account…" : "Review terms & create customer account"}
              </button>
            </form>
          </>
        )}

        <div className="mt-6 text-sm text-slate-600">
          Already have an account?{" "}
          <Link to={loginPath} className="font-medium text-[#1B4332] hover:underline" data-testid="register-go-login-link">
            Sign in
          </Link>
        </div>

        {isBiz && <BusinessDemoCard showLoginLink />}

        <div className="mt-6 pt-5 border-t border-slate-100 text-xs text-slate-500">
          {switchHelp.text}{" "}
          <Link
            to={switchHelp.to}
            data-testid="register-switch-audience-link"
            className="font-medium text-[#1B4332] hover:underline"
          >
            {switchHelp.action}
          </Link>
        </div>
      </div>

      <LegalAcceptanceModal
        open={legalOpen}
        onClose={() => setLegalOpen(false)}
        onAccept={finalSubmit}
        mode={isBiz ? "business" : "customer"}
      />
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testId, className = "" }) {
  return (
    <label className={className}>
      <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{label}</span>
      <input
        data-testid={testId}
        type={type}
        required={required}
        value={value}
        onChange={onChange}
        className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300 focus:border-[#2D6A4F] focus:ring-1 focus:ring-[#2D6A4F] outline-none"
      />
    </label>
  );
}

function RememberDeviceField({ checked, onChange, className = "" }) {
  return (
    <label className={`flex items-start gap-2.5 text-sm text-slate-700 cursor-pointer ${className}`}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        data-testid="register-remember-device"
        className="mt-0.5 w-4 h-4 rounded border-slate-400 text-emerald-700 focus:ring-emerald-600"
      />
      <span>
        Remember this device for 30 days.{" "}
        <span className="text-xs text-slate-500">
          Stay signed in here: you can revoke any device anytime from Settings.
        </span>
      </span>
    </label>
  );
}

function LegalNotice({ mode = "customer", className = "" }) {
  return (
    <div
      data-testid="register-legal-notice"
      className={`flex items-start gap-2 text-xs text-slate-600 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 ${className}`}
    >
      <ShieldCheck className="w-4 h-4 text-emerald-700 flex-shrink-0 mt-0.5" />
      <span>
        Next, you&apos;ll be asked to accept the
        <Link to="/terms" target="_blank" className="underline mx-1">Terms</Link>
        and
        <Link to="/acceptable-use" target="_blank" className="underline mx-1">Acceptable Use Policy</Link>
        {mode === "business" && (<>
          plus the
          <Link to="/business-terms" target="_blank" className="underline mx-1">Business Terms</Link>
        </>)}
        before your account is created. You&apos;ll also be given the
        <Link to="/privacy" target="_blank" className="underline mx-1">Privacy Policy</Link>
        to read. Optional cookies are chosen separately (see the
        <Link to="/cookies" target="_blank" className="underline mx-1">Cookie Policy</Link>).
      </span>
    </div>
  );
}

function ServiceTypeSelect({ value, onChange }) {
  const onPick = (e) => {
    const v = e.target.value;
    // Disabled options can't be selected via the dropdown directly because
    // we render them with `disabled`, but defensive guard for accessibility
    // tools that ignore the attribute.
    if (COMING_SOON_SERVICE_TYPES.some((s) => s.value === v)) {
      toast.message(COMING_SOON_MESSAGE);
      return;
    }
    onChange(v);
  };
  return (
    <label className="sm:col-span-2">
      <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
        What do you rent out?
      </span>
      <select
        data-testid="register-service-type"
        value={value}
        onChange={onPick}
        className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300 focus:border-[#2D6A4F] focus:ring-1 focus:ring-[#2D6A4F] outline-none bg-white"
      >
        <optgroup label="Available now">
          {ACTIVE_SERVICE_TYPES.map((s) => (
            <option key={s.value} value={s.value} data-testid={`register-service-${s.value}`}>
              {s.label}
            </option>
          ))}
        </optgroup>
        <optgroup label="Coming soon">
          {COMING_SOON_SERVICE_TYPES.map((s) => (
            <option
              key={s.value}
              value={s.value}
              disabled
              data-testid={`register-service-${s.value}-disabled`}
            >
              {s.label}: Coming soon
            </option>
          ))}
        </optgroup>
      </select>
      <span className="mt-1 inline-flex items-center gap-1 text-[11px] text-slate-500">
        <Lock className="w-3 h-3" /> ConfirmPH is starting with car and motorbike rentals first.
      </span>
    </label>
  );
}
